import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import { VibeTenantClusterStatusSchema } from "./zadmin-vibe-tenant-cluster-list-item";

/**
 * Ask ONE serving tenant cluster to roll every Nomad/Consul server.
 *
 * A server roll happens only when what a server boots differs from the server
 * launch template's `$Latest`. A change that ships by agent self-upgrade never
 * moves that template, so nothing ever rolls the servers and an operator has no
 * lever to make it. This is the lever: the request stamps a SERVER ROLL
 * GENERATION on the cluster, the provisioner renders it as one inert line in the
 * server boot script, and the next converge — which the request triggers, the
 * way a forced converge does — mints a template version every server is stale
 * against. The roll itself is the existing one, unchanged: surge, raft-aware
 * retirement one server at a time, zone ordering, the settle judge.
 *
 * Asynchronous by construction: the response says a roll WILL happen, never that
 * it has. The roster's `serverRoll` says whether it has settled.
 */
export const AdminVibeTenantClusterRequestServerRollBodySchema = z
  .object({
    organizationId: z.string().min(1),
    /**
     * Why the operator wants the servers rolled. Required for the reason a forced
     * converge's is: it is written into the operator-visible `statusReason` of
     * the converge it triggers, and kept on the cluster beside the generation.
     */
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * Discriminated outcome. Every variant is a SUCCESS — each is a true answer to
 * "will this cluster's servers roll now" — so only a persistence fault is an
 * error.
 *
 *   - requested: a new generation is stamped and the cluster is moved HEALTHY →
 *     DEGRADED in the same write, so the next reconcile tick converges it.
 *   - already_pending: a generation is stamped and has not settled. Nothing is
 *     written — a second generation would only restart a roll in flight.
 *   - reconcile_paused: an operator is holding the cluster out of the sweep, so
 *     a stamped generation would never be converged. Nothing is written.
 *   - not_settled: the cluster is not HEALTHY. Only a HEALTHY cluster has no
 *     converge in flight, and a converge that began before the request carries
 *     no generation, so accepting one here could let it settle over servers that
 *     never rolled. Nothing is written; ask again once the cluster is HEALTHY.
 *   - not_found: no tenant cluster for that organization.
 */
export const AdminVibeTenantClusterRequestServerRollOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("requested"),
    requestedAt: z.string().datetime(),
    reason: z.string()
  }),
  z.object({
    kind: z.literal("already_pending"),
    requestedAt: z.string().datetime(),
    reason: z.string().nullable()
  }),
  z.object({
    kind: z.literal("reconcile_paused"),
    status: VibeTenantClusterStatusSchema,
    pausedReason: z.string().nullable()
  }),
  z.object({ kind: z.literal("not_settled"), status: VibeTenantClusterStatusSchema }),
  z.object({ kind: z.literal("not_found") })
]);

/** `AdminVibeTenantClusterRequestServerRollOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterRequestServerRollOutcomeReadSchema = z.union([
  AdminVibeTenantClusterRequestServerRollOutcomeSchema,
  z.looseObject({
    kind: unlistedDiscriminantSchema(AdminVibeTenantClusterRequestServerRollOutcomeSchema)
  })
]);

export type AdminVibeTenantClusterRequestServerRollBody = z.infer<
  typeof AdminVibeTenantClusterRequestServerRollBodySchema
>;
export type AdminVibeTenantClusterRequestServerRollOutcome = z.infer<
  typeof AdminVibeTenantClusterRequestServerRollOutcomeSchema
>;
